import type {
  PreslinkCreationPresentationContext,
  WriteFileFromBase64Outcome,
} from "../factset/intentBridgeService";

/**
 * Excel refuses to open a workbook whose full path exceeds 218 characters, and
 * it fails at open time rather than at save time. Checking before any network
 * call keeps a doomed request from costing the user a ChartCreator round-trip.
 */
export const EXCEL_MAX_FULL_PATH_LENGTH = 218;

/** Write attempts per workbook before a PresLink save gives up on finding a free name. */
export const PRESLINK_WORKBOOK_NAME_ATTEMPTS = 5;

/** FactSet reports an `overwrite: false` collision either as already_exists or as a generic IO failure. */
const WORKBOOK_COLLISION_PATTERN = /already exists|file exists|unexpected io error/i;

export function isPreslinkWorkbookWriteCollision(outcome: WriteFileFromBase64Outcome): boolean {
  return (
    outcome.status === "already_exists" ||
    (outcome.status === "failed" && WORKBOOK_COLLISION_PATTERN.test(outcome.message))
  );
}

/** Minimum identifier segment worth keeping; below this the segment is dropped. */
const MIN_IDENTIFIER_SEGMENT_LENGTH = 3;

export type PreslinkDeckLocationKind = "windows" | "posix" | "unc";

export interface PreslinkDeckLocation {
  kind: PreslinkDeckLocationKind;
  /** The host-reported folder, used verbatim — never re-encoded or normalized. */
  folderPath: string;
  separator: "\\" | "/";
}

export class PreslinkPreflightError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = "PreslinkPreflightError";
    this.code = code;
    Object.setPrototypeOf(this, PreslinkPreflightError.prototype);
  }
}

const SAVE_DECK_MESSAGE =
  "Save the presentation to a local or network folder before creating a linked FactSet chart. " +
  "SharePoint, OneDrive, and other online locations are not supported.";

function looksLikeUrl(value: string): boolean {
  // Covers http(s), file:, ms-onedrive:, and the SharePoint/OneDrive sync paths
  // PowerPoint reports as URLs. A bare drive letter ("C:\") is not a scheme
  // because a scheme needs at least two characters before the colon.
  return /^[A-Za-z][A-Za-z0-9+.-]+:\/\//.test(value) || /^[A-Za-z][A-Za-z0-9+.-]{1,}:(?![\\/])/.test(value);
}

/**
 * Classifies the deck's folder. The path is returned exactly as the host gave
 * it: Preslink stores it inside the tag, and a path the add-in "cleaned up"
 * would no longer match what the host resolves later.
 */
export function classifyPreslinkDeckLocation(rawPath: string): PreslinkDeckLocation {
  const path = (rawPath ?? "").trim();
  if (!path) {
    throw new PreslinkPreflightError("preslink_deck_never_saved", SAVE_DECK_MESSAGE);
  }
  if (looksLikeUrl(path)) {
    throw new PreslinkPreflightError("preslink_deck_url_location", SAVE_DECK_MESSAGE);
  }
  if (/^\\\\[^\\]+\\[^\\]+/.test(path)) {
    return { kind: "unc", folderPath: path.replace(/\\+$/, ""), separator: "\\" };
  }
  if (/^[A-Za-z]:[\\/]/.test(path)) {
    return { kind: "windows", folderPath: path.replace(/[\\/]+$/, ""), separator: "\\" };
  }
  if (path.startsWith("/")) {
    return { kind: "posix", folderPath: path.replace(/\/+$/, ""), separator: "/" };
  }
  throw new PreslinkPreflightError("preslink_deck_unsupported_location", SAVE_DECK_MESSAGE);
}

/**
 * Turn-start deck check, reused by every chart in the turn. The deck needs a
 * file on a local or network folder for the workbook to sit beside; unsaved
 * edits since that save do not matter, because the workbook is written to the
 * folder and the chart lives in the open deck either way.
 */
export function assertPreslinkDeckUsable(
  context: PreslinkCreationPresentationContext,
): PreslinkDeckLocation {
  const location = classifyPreslinkDeckLocation(context.path);
  if (!(context.fullName ?? "").trim()) {
    throw new PreslinkPreflightError("preslink_deck_never_saved", SAVE_DECK_MESSAGE);
  }
  if (context.readOnly === true) {
    throw new PreslinkPreflightError(
      "preslink_deck_read_only",
      "This presentation is read-only, so a linked FactSet chart cannot be added. Open an editable copy and try again.",
    );
  }
  return location;
}

export function sanitizePreslinkIdentifierSegment(identifiers: readonly string[]): string {
  return identifiers
    .map((identifier) => (identifier ?? "").trim())
    .filter((identifier) => identifier.length > 0)
    .map((identifier) => identifier.replace(/[^A-Za-z0-9._-]/g, "-"))
    .join("_");
}

export function formatPreslinkTimestamp(date: Date): string {
  const pad = (value: number, width = 2) => String(value).padStart(width, "0");
  return (
    `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}` +
    `_${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}`
  );
}

export interface PreslinkFileNameInput {
  location: PreslinkDeckLocation;
  identifiers: readonly string[];
  chartCode: string;
  timestamp: Date;
}

export interface PreslinkFileNameResult {
  fileName: string;
  fullPath: string;
}

/**
 * Builds `<identifiers>_<ChartCode>_<YYYYMMDD_HHMMSS>.xlsx` under the deck's
 * folder. Only the identifier segment is shortened: the chart code and the
 * timestamp are what make the name meaningful and unique, so losing either
 * would be worse than losing part of a ticker list.
 */
export function buildPreslinkWorkbookFileName(
  input: PreslinkFileNameInput,
): PreslinkFileNameResult {
  const { location, chartCode, timestamp } = input;
  const stamp = formatPreslinkTimestamp(timestamp);
  const suffix = `${chartCode}_${stamp}.xlsx`;
  const prefix = `${location.folderPath}${location.separator}`;
  const budget = EXCEL_MAX_FULL_PATH_LENGTH - prefix.length - suffix.length;

  if (budget < 0) {
    throw new PreslinkPreflightError(
      "preslink_path_too_long",
      `The presentation's folder path is too long for Excel: even the shortest workbook name would exceed ${EXCEL_MAX_FULL_PATH_LENGTH} characters. Move the presentation to a shorter folder path.`,
    );
  }

  const identifierSegment = sanitizePreslinkIdentifierSegment(input.identifiers);
  // The separator between identifiers and chart code costs one character, so a
  // segment only earns its place if something readable survives.
  const available = budget - 1;
  let effective = identifierSegment;
  if (identifierSegment.length > available) {
    effective = available >= MIN_IDENTIFIER_SEGMENT_LENGTH
      ? identifierSegment.slice(0, available).replace(/[._-]+$/, "")
      : "";
  }

  const fileName = effective ? `${effective}_${suffix}` : suffix;
  return { fileName, fullPath: `${prefix}${fileName}` };
}

/**
 * True when a file name has the shape `buildPreslinkWorkbookFileName` gives
 * this chart code. A PresLink refresh can return a name of the same shape with
 * a newer timestamp, so this only stands in for charts that do not record the
 * name they were created with.
 */
export function isCreatedPreslinkWorkbookName(fileName: string, chartCode: string): boolean {
  const code = chartCode.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(?:^|_)${code}_\\d{8}_\\d{6}\\.xlsx$`, "i").test(fileName);
}

/**
 * Monotonic UTC second allocator. Collision retry rebuilds the name from the
 * next second, and two charts created back-to-back in one turn must not land
 * on the same name, so the clock is only ever allowed to move forward.
 */
export function createPreslinkTimestampSequence(now: () => number = Date.now) {
  let lastSecond = 0;
  return {
    next(): Date {
      const current = Math.floor(now() / 1000);
      lastSecond = current > lastSecond ? current : lastSecond + 1;
      return new Date(lastSecond * 1000);
    },
  };
}
